import { useState, useEffect } from 'react';
import { getPricingConfig, savePricingConfig } from '../../utils/accountStorage';
import { DEFAULT_PRICING, PLAN_KEYS } from '../../config/pricingDefaults';

function planToForm(plan) {
  return {
    ...plan,
    features: (plan.features || []).join('\n'),
    missing:  (plan.missing  || []).join('\n'),
  };
}

function formToPlan(form) {
  return {
    ...form,
    order:     Number(form.order),
    highlight: Boolean(form.highlight),
    features:  form.features.split('\n').map(s => s.trim()).filter(Boolean),
    missing:   form.missing.split('\n').map(s => s.trim()).filter(Boolean),
  };
}

function AdminPricingEditor() {
  const [activePlan, setActivePlan] = useState('basic');
  const [forms, setForms]           = useState(() =>
    Object.fromEntries(PLAN_KEYS.map(k => [k, planToForm(DEFAULT_PRICING[k])]))
  );
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    getPricingConfig().then(remote => {
      if (!remote) return;
      setForms(prev => {
        const next = { ...prev };
        for (const k of PLAN_KEYS) {
          if (remote[k]) {
            next[k] = planToForm({ ...DEFAULT_PRICING[k], ...remote[k] });
          }
        }
        return next;
      });
    });
  }, []);

  const set = (field, value) =>
    setForms(f => ({ ...f, [activePlan]: { ...f[activePlan], [field]: value } }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...Object.fromEntries(PLAN_KEYS.map(k => [k, formToPlan(forms[k])])),
        _updatedAt: new Date().toISOString(),
      };
      await savePricingConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const f = forms[activePlan];

  const inputStyle = {
    width: '100%', padding: '9px 11px', borderRadius: 9,
    border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: 0.7,
    display: 'block', marginBottom: 4,
  };
  const sectionStyle = { marginBottom: 16 };

  return (
    <div>
      {/* Plan tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {PLAN_KEYS.map(k => (
          <button
            key={k}
            onClick={() => setActivePlan(k)}
            style={{
              flex: 1, padding: '8px 4px', borderRadius: 10, cursor: 'pointer',
              border: activePlan === k ? `2px solid #0A7C8A` : '1.5px solid #E5E7EB',
              background: activePlan === k ? '#0A7C8A18' : '#fff',
              color: activePlan === k ? '#0A7C8A' : '#6B7280',
              fontSize: 12, fontWeight: 800,
            }}
          >
            {forms[k].name || k}
          </button>
        ))}
      </div>

      {/* Fields */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Display Name</label>
        <input style={inputStyle} value={f.name} onChange={e => set('name', e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Price</label>
          <input style={inputStyle} value={f.price} onChange={e => set('price', e.target.value)} placeholder="e.g. $2.99 or Free" />
        </div>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>Price Note</label>
          <input style={inputStyle} value={f.priceNote} onChange={e => set('priceNote', e.target.value)} placeholder="e.g. per month" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>Badge Text</label>
          <input style={inputStyle} value={f.badge} onChange={e => set('badge', e.target.value)} placeholder="e.g. Most Popular (leave blank for none)" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Highlight card</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6 }}>
            <input type="checkbox" checked={!!f.highlight} onChange={e => set('highlight', e.target.checked)} style={{ accentColor: '#0A7C8A', width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: '#374151' }}>Yes</span>
          </label>
        </div>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Features (one per line)</label>
        <textarea
          style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
          value={f.features}
          onChange={e => set('features', e.target.value)}
          placeholder="5 AI receipt scans / month&#10;Up to 3 expense reports&#10;..."
        />
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Unavailable features — shown greyed out (one per line)</label>
        <textarea
          style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
          value={f.missing}
          onChange={e => set('missing', e.target.value)}
          placeholder="Excel & Word export&#10;Report sharing&#10;..."
        />
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', color: '#DC2626', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
          background: saved ? '#10B981' : 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
          color: '#fff', fontSize: 14, fontWeight: 700,
        }}
      >
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Pricing'}
      </button>

      <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 10 }}>
        Changes are live immediately on the public pricing page.
      </p>
    </div>
  );
}

export default AdminPricingEditor;
